import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  BadgeCheck,
  BadgeDollarSign,
  BarChart3,
  Briefcase,
  Building2,
  Car,
  Check,
  ChevronDown,
  ClipboardList,
  CreditCard,
  Database,
  Eye,
  FileCheck,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  IdCard,
  Info,
  Landmark,
  type LucideIcon,
  Mail,
  MapPin,
  Network,
  PenTool,
  Receipt,
  ScanFace,
  ScanText,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  SquarePen,
  Stamp,
  UserCheck,
  Wallet,
  X,
} from 'lucide-react';
// Aliased: the bare names would shadow the DOM's MouseEvent/PointerEvent, which
// the click-outside listener below needs.
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { useApiCatalogue } from '../../api/useApiCatalogue';
import type { ApiCatalogueEntry, WorkflowStepDto } from '../../schemas/workflowSchemas';
import { CatalogIcon } from '../../stepIcons';
import { STEP_TYPE_CATALOGUE_TAGS } from '../../types';

/** Steps whose API has no category yet — they must still be reachable, not vanish. */
const UNCATEGORISED = 'Other';

type CatalogEntry = {
  id: string;
  name: string;
  icon: LucideIcon;
  price: string;
  /** Replaces the default "per completed check" tooltip when a step bills differently. */
  priceNote?: string;
  stepType?: string;
};

/**
 * Steps offered even when the catalogue has no row for them yet.
 *
 * Document Template DOES have a catalogue entry (tag "Template Management API",
 * Console type 9) — but it was added after the first deployments, so a database
 * seeded before that restart has no row and the tile would vanish. The bypass keeps
 * it visible; the price still comes from the catalogue once the seeder has run.
 */
const CATALOGUE_INDEPENDENT_STEP_TYPES = new Set<string>([
  'document_template',
  'esign_template',
  // Questionnaire calls no provider at all, so it has no catalogue row anywhere and never will —
  // for the other two the bypass is a seeding workaround, but here it is permanent.
  'questionnaire',
  // Email Verification is ours end to end: WE mail the code, so there is no SignCare API behind it
  // and no catalogue row to gate the tile on. Permanent for the same reason as Questionnaire —
  // though unlike it, this one WOULD gain a row if the step is ever priced (see STEP_TYPE_META).
  'email_verification',
]);

/**
 * Superseded steps — hidden from the picker, still rendered if a workflow has one.
 *
 * Both are placeholder tiles with no settings panel and no runtime, and both were
 * invisible until the Template and eSign catalogue rows were added: their tags
 * (`Template Management API`, `eSign API Suite`) suddenly existed, so the gate that
 * had been hiding them opened. Their jobs now belong to Document Template and eSign
 * Document Template, and three near-identical tiles is worse than two.
 *
 * NOT removed from STEP_CATALOG: a workflow that already contains one still needs a
 * row for it, or the step would disappear from the list while staying in the data.
 */
const SUPERSEDED_STEP_TYPES = new Set<string>(['document_upload', 'esign']);

/**
 * Step types that may appear MORE THAN ONCE in a workflow.
 *
 * Every other type is once-only, and rightly so: a workflow that verifies the same PAN twice is a
 * misconfiguration, and the picker hides an added type to say so.
 *
 * Questionnaire is different in kind. It is not a check with a result — it is a place to ask
 * questions, and where those questions sit in the journey is the point. "Ask about trading experience
 * BEFORE Aadhaar, then about tax residency after Bank" is a normal requirement, and it needs two
 * separate steps because a single one can only occupy one position. Each keeps its own attached
 * questionnaire in its own settings, so they are genuinely independent.
 *
 * A repeatable type stays in the picker after being added, so it can be added again.
 */
const REPEATABLE_STEP_TYPES = new Set<string>(['questionnaire']);

/**
 * Whether a workflow may hold more than one step of this type.
 *
 * Exported because the picker is not the only place that has to know. The editor's add handler
 * SELECTS an existing step instead of adding when the type is already present — which is right for a
 * once-only type and wrong for a repeatable one, where clicking the tile a second time looked like a
 * dead button.
 */
export function isRepeatableStepType(stepType: string): boolean {
  return REPEATABLE_STEP_TYPES.has(stepType);
}

/**
 * An added step, as the catalog needs to identify it.
 *
 * Identity is the step ID, not its type — and that is now load-bearing rather than defensive. It
 * used to say the two were interchangeable because every type was once-only; Questionnaire is
 * repeatable (see REPEATABLE_STEP_TYPES), so a workflow can hold several steps of one type and only
 * the id tells them apart. Keying rows by id is what made that change a one-line filter instead of a
 * rewrite.
 *
 * `label` overrides the catalogue entry's name for this row, so a Document Template
 * step can read as the document it generates rather than as its type.
 */
export type ActiveStep = {
  id: string;
  stepType: string;
  label?: string;
  /**
   * The step's ACTUAL cost, when it can only be known from its configuration.
   *
   * An eSign step is billed per signer and each method is priced differently, so the
   * catalogue row for the tag (which is one method's price) is not this step's price.
   * Showing that number on the row is simply wrong — hence the override.
   */
  price?: number;
  priceNote?: string;
};

const STEP_CATALOG: CatalogEntry[] = [
  {
    id: 'questionnaire',
    name: 'Questionnaire',
    icon: ClipboardList,
    // Genuinely free — no provider call, no catalogue row. Unlike the other tiles this price is
    // not a placeholder waiting for the tenant's catalogue.
    price: '0.00',
    priceNote: 'Free — this step collects answers and calls no verification provider',
    stepType: 'questionnaire',
  },
  {
    id: 'emailVerification',
    name: 'Email Verification',
    icon: Mail,
    // Genuinely free, like Questionnaire — but for a different reason. This step DOES send
    // something; we just send it ourselves, through Studio's own SMTP, so no verification provider
    // is called. Not a placeholder waiting for a catalogue price.
    price: '0.00',
    priceNote: 'Free — SignCare sends the code itself and calls no verification provider',
    stepType: 'email_verification',
  },
  {
    id: 'documentTemplate',
    name: 'Document Template',
    icon: FileCheck,
    // Placeholder only — withRealPrice replaces this with the tenant's catalogue
    // price. Shown while the catalogue is still loading.
    price: '10.00',
    stepType: 'document_template',
  },
  {
    id: 'esignTemplate',
    name: 'eSign Document Template',
    icon: SquarePen,
    // Zero until signatures are placed — an eSign step with nobody signing costs
    // nothing, and the row then shows the real total (see ActiveStep.price).
    price: '0.00',
    priceNote: 'Billed per signer once their signature is placed — ₹15.00 to ₹25.00 by method',
    stepType: 'esign_template',
  },
  {
    id: 'pan',
    name: 'PAN Verification',
    icon: IdCard,
    price: '10.00',
    stepType: 'pan_verification',
  },
  {
    id: 'aadhaar',
    name: 'Aadhaar Verification',
    icon: FileText,
    price: '10.00',
    stepType: 'aadhaar_otp',
  },
  {
    id: 'face',
    name: 'Face Match',
    icon: ScanFace,
    price: '5.00',
    stepType: 'face_match',
  },
  {
    id: 'liveness',
    name: 'Face Liveness',
    icon: Eye,
    price: '5.00',
    stepType: 'liveness',
  },
  {
    id: 'bank',
    name: 'Bank Account Verification',
    icon: Building2,
    price: '7.50',
    stepType: 'bank_account',
  },
  { id: 'kra', name: 'KRA Check', icon: Fingerprint, price: '10.00', stepType: 'kra_check' },
  {
    id: 'doc',
    name: 'Document Upload (Signature)',
    icon: PenTool,
    price: '10.00',
    stepType: 'document_upload',
  },
  { id: 'esign', name: 'eSign Agreement', icon: SquarePen, price: '10.00', stepType: 'esign' },
  { id: 'esign2', name: 'eSign Agreement', icon: SquarePen, price: '10.00' },
  { id: 'esign3', name: 'eSign Agreement', icon: SquarePen, price: '10.00' },
  { id: 'esign4', name: 'eSign Agreement', icon: SquarePen, price: '10.00' },
  { id: 'additional', name: 'Additional', icon: FileText, price: '10.00' },
  {
    id: 'idVerification',
    name: 'ID Verification',
    icon: IdCard,
    price: '10.00',
    stepType: 'id_verification',
  },
  {
    id: 'utilityBill',
    name: 'Utility Bill Verification',
    icon: FileText,
    price: '7.50',
    stepType: 'utility_bill',
  },
  {
    id: 'rationCard',
    name: 'Ration Card Verification',
    icon: FileText,
    price: '10.00',
    stepType: 'ration_card',
  },
  { id: 'gst', name: 'GST Verification', icon: Receipt, price: '10.00', stepType: 'gstin' },
  { id: 'corporate', name: 'Corporate Verification', icon: Building2, price: '10.00' },
  {
    id: 'msme',
    name: 'MSME Verification (Udyam)',
    icon: Building2,
    price: '10.00',
    stepType: 'msme',
  },
  { id: 'businessId', name: 'Business ID Verification', icon: Briefcase, price: '10.00' },
  { id: 'professional', name: 'Professional Verification', icon: BadgeCheck, price: '10.00' },
  { id: 'ocr', name: 'OCR', icon: ScanText, price: '10.00' },
  { id: 'ocrCheque', name: 'OCR-Cheque', icon: ScanText, price: '10.00' },
  { id: 'ocrGst', name: 'OCR-GST', icon: ScanText, price: '10.00' },
  { id: 'ocrCoi', name: 'OCR- COI', icon: ScanText, price: '10.00' },
  { id: 'form16', name: 'Form 16 - OCR', icon: ScanText, price: '10.00' },
  { id: 'fuzzyMatch', name: 'Fuzzy Match', icon: Search, price: '10.00' },
  { id: 'ifsc', name: 'IFSC Verification', icon: Landmark, price: '10.00' },
  { id: 'vehicleRc', name: 'Vehicle RC Verification', icon: Car, price: '10.00' },
  { id: 'vehicleToll', name: 'Vehicle RC to Toll', icon: Car, price: '10.00' },
  { id: 'uan', name: 'UAN based Verification', icon: UserCheck, price: '10.00' },
  { id: 'mobileUan', name: 'Mobile to UAN', icon: Smartphone, price: '10.00' },
  { id: 'employment', name: 'UAN to Employment History', icon: Briefcase, price: '10.00' },
  { id: 'estamp', name: 'eStamp', icon: Stamp, price: '10.00' },
  { id: 'geoLocation', name: 'GeoLocation', icon: MapPin, price: '10.00' },
  { id: 'itrGst', name: 'ITR/GST', icon: FileSpreadsheet, price: '10.00' },
  { id: 'itrAnalysis', name: 'ITR Analysis', icon: BarChart3, price: '10.00' },
  { id: 'gstAnalysis', name: 'GST Analysis', icon: BarChart3, price: '10.00' },
  {
    id: 'bankFetch',
    name: 'Bank Statement fetch and analysis',
    icon: FileSpreadsheet,
    price: '10.00',
  },
  { id: 'bsa', name: 'Bank Statement Analysis (BSA)', icon: BarChart3, price: '10.00' },
  { id: 'accountAggregator', name: 'Account Aggregator', icon: Network, price: '10.00' },
  { id: 'creditScore', name: 'Credit Score', icon: BadgeDollarSign, price: '10.00' },
  { id: 'experian', name: 'Experian Credit Bureau', icon: ShieldCheck, price: '10.00' },
  { id: 'equifax', name: 'Equifax Credit Bureau', icon: ShieldCheck, price: '10.00' },
  { id: 'cersai', name: 'Cersai based data fetch', icon: Database, price: '10.00' },
  { id: 'ckyc', name: 'CKYC', icon: FileCheck, price: '10.00' },
  {
    id: 'mobileVerification',
    name: 'Mobile Number Verification',
    icon: Smartphone,
    price: '10.00',
  },
  { id: 'emailVerification', name: 'Email Verification', icon: Mail, price: '10.00' },
  { id: 'upiVerification', name: 'UPI based Verification', icon: Wallet, price: '10.00' },
  { id: 'enach', name: 'eNach', icon: CreditCard, price: '10.00' },
];

// ── Click-vs-drag guard ───────────────────────────────────────────────────────
// An active row is both a drag handle and a click target, so finishing a
// reorder must not also count as a click. dnd-kit's 5px activation distance
// decides when a gesture becomes a drag, but the browser still fires a click on
// mouseup afterwards. This measures how far the pointer travelled and treats
// anything past the same 5px threshold as a drag, not a click.
const DRAG_SLOP_PX = 5;

function useClickGuard() {
  const origin = useRef<{ x: number; y: number } | null>(null);
  return useMemo(
    () => ({
      onPointerDown(e: ReactPointerEvent) {
        origin.current = { x: e.clientX, y: e.clientY };
      },
      wasDrag(e: ReactMouseEvent) {
        const start = origin.current;
        origin.current = null;
        if (!start) return false;
        return Math.hypot(e.clientX - start.x, e.clientY - start.y) > DRAG_SLOP_PX;
      },
    }),
    [],
  );
}

// ── Price + info ──────────────────────────────────────────────────────────────
// The designer's sidebar ends every row with the price and an ⓘ, replacing the
// add/remove tick we used to have. Hovering the ⓘ explains what the number is,
// reusing the same CSS-only tooltip as the step icons. Deliberately a <span>,
// not a <button> — the row itself is the button, and nesting one inside another
// is invalid HTML.
function PriceAndInfo({ price, note }: { price: string; note?: string }) {
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span className="min-w-[42px] text-right text-[12px] font-medium text-gray66 xl:text-[14px]">
        ₹ {price}
      </span>
      <span className="sc-step-tooltip-wrapper">
        <Info className="h-4 w-4 text-black-logo transition-colors hover:text-primary" />
        <span className="sc-step-tooltip" role="tooltip">
          {/* Not every step is "per completed check" — eSign is billed per signer,
              and each signature method has its own price. */}
          {note ?? `₹${price} per completed check`}
          <span className="sc-step-tooltip__arrow" />
        </span>
      </span>
    </span>
  );
}

// ── Sortable active row ───────────────────────────────────────────────────────

function SortableActiveRow({
  sortableId,
  entry,
  isSelected,
  isLast,
  onSelect,
  onRemove,
  canRemove,
  readOnly = false,
}: {
  /** The STEP's id — two steps of the same type must sort independently. */
  sortableId: string;
  entry: CatalogEntry;
  isSelected: boolean;
  isLast: boolean;
  // Opens this step's configuration.
  onSelect: () => void;
  // Takes the step back out of the workflow (the marker's ✕ affordance).
  onRemove: () => void;
  /**
   * False when this is the ONLY step in the workflow. The editor refuses that
   * removal (a workflow with no steps verifies nothing, and the backend rejects
   * publishing one), so the ✕ is not offered at all — an affordance that can only
   * produce a warning is worse than no affordance. The tick still shows, with a
   * title explaining why.
   */
  canRemove: boolean;
  readOnly?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: readOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const clickGuard = useClickGuard();
  const Icon = entry.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('relative mb-2 flex items-center', isDragging && 'z-50 opacity-70')}
    >
      {/* Dotted vertical connector */}
      {!isLast && (
        <div className="absolute start-[9px] top-1/2 z-0 h-[calc(100%+8px)] border-l-2 border-dotted border-primary transition-colors duration-300" />
      )}

      {/* In-workflow marker. Shows a tick, and on hover turns into a red ✕ that
          removes the step — the designer's affordance (WorkflowStepsSidebar.tsx
          `group/stepnum`). It replaced the position number: the sequence is already
          conveyed by the order plus the dotted connector, and putting the remove
          control on the marker means adding and removing both live in the same
          place. Same 20px/12px geometry as their `__step-number` so the connector
          still lines up. Read-only keeps the tick as a plain indicator. */}
      {readOnly || !canRemove ? (
        <div
          className="relative z-10 me-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-white"
          title={
            readOnly
              ? 'In this workflow'
              : 'A workflow needs at least one step — add another before removing this one.'
          }
        >
          <Check className="h-[11px] w-[11px] stroke-[2.5]" />
        </div>
      ) : (
        <button
          type="button"
          // The row next to this is a drag handle; without stopPropagation the
          // pointerdown here would start a reorder gesture instead of a click.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${entry.name} from this workflow`}
          title="Remove step"
          className="group/marker relative z-10 me-1 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-primary text-white transition-colors hover:bg-[#DC2626]"
        >
          <Check className="h-[11px] w-[11px] stroke-[2.5] group-hover/marker:hidden" />
          <X className="hidden h-[11px] w-[11px] stroke-[2.5] group-hover/marker:block" />
        </button>
      )}

      {/* Row — click opens the step's config (Method / Settings / Risk Rules),
          drag reorders. The card is the drag handle, so the click is guarded
          against firing at the end of a reorder (see clickGuard). */}
      <button
        type="button"
        onPointerDown={clickGuard.onPointerDown}
        onClick={(e) => {
          if (clickGuard.wasDrag(e)) return;
          onSelect();
        }}
        className={cn(
          'flex flex-1 items-center justify-between rounded-md border bg-white px-[clamp(8px,0.85vw,12px)] py-[clamp(8px,0.75vw,10px)] text-start transition-all',
          isSelected
            ? 'border-primary shadow-sm ring-1 ring-primary'
            : 'border-[#E5E7EB] hover:border-primary/40',
          isDragging && 'shadow-md',
          !readOnly && 'cursor-grab active:cursor-grabbing',
        )}
        {...(readOnly ? {} : attributes)}
        {...(readOnly ? {} : listeners)}
      >
        <span className="flex flex-1 items-center gap-2 overflow-hidden text-primary">
          <CatalogIcon catalogueId={entry.id} fallback={Icon} className="h-6 w-6" />
          {/* `title` so a name too long for the rail is still readable on hover. It matters now that
              a step can be labelled with what it has ATTACHED — two Questionnaire steps are told
              apart only by that label, so a truncated one with no tooltip would leave them
              indistinguishable. */}
          <span className="truncate text-[clamp(11px,0.86vw,14px)] font-medium" title={entry.name}>
            {entry.name}
          </span>
        </span>
        <PriceAndInfo price={entry.price} note={entry.priceNote} />
      </button>
    </div>
  );
}

// ── Static inactive row ───────────────────────────────────────────────────────

function InactiveRow({
  entry,
  isLast,
  isSelected,
  onSelect,
  readOnly = false,
}: {
  entry: CatalogEntry;
  isLast: boolean;
  isSelected: boolean;
  // Adds the step AND selects it (see handleSelectStepType) — there's no
  // separate add control now that the whole row is the target.
  onSelect: () => void;
  readOnly?: boolean;
}) {
  const Icon = entry.icon;
  // In read-only mode inactive rows are not clickable — selecting one would
  // ADD the step to the workflow, which is an edit.
  const clickable = !!entry.stepType && !readOnly;

  return (
    <div className="relative mb-2 flex items-center">
      {!isLast && (
        <div className="absolute start-[9px] top-1/2 z-0 h-[calc(100%+8px)] border-l-2 border-dotted border-[#E5E7EB]" />
      )}
      {/* The hollow marker also adds the step, so the marker column is add/remove
          on both sides of the pair. It delegates to the row's own handler, so the
          dependency check and read-only rules are the exact same code path. */}
      {clickable ? (
        <button
          type="button"
          onClick={onSelect}
          aria-label={`Add ${entry.name} to this workflow`}
          title="Add step"
          className="relative z-10 mx-1 h-3 w-3 shrink-0 cursor-pointer rounded-full border-none bg-[#E5E7EB] p-0 transition-colors hover:bg-primary/30"
        />
      ) : (
        <div className="relative z-10 mx-1 h-3 w-3 shrink-0 rounded-full bg-[#E5E7EB]" />
      )}

      {/* Tapping anywhere on the row adds the step and opens its config, matching
          the designer's sidebar — there's no separate add control. */}
      <button
        type="button"
        aria-label={`Add ${entry.name}`}
        onClick={clickable ? onSelect : undefined}
        className={cn(
          'flex flex-1 items-center justify-between rounded-md border border-transparent bg-transparent px-[clamp(8px,0.85vw,12px)] py-[clamp(8px,0.75vw,10px)] text-start transition-all',
          clickable ? 'cursor-pointer hover:border-[#E5E7EB] hover:bg-[#F9FAFB]' : 'cursor-default',
          isSelected &&
            'border-primary bg-white shadow-sm ring-1 ring-primary hover:border-primary hover:bg-white',
        )}
      >
        <span className="flex items-center gap-2 text-[#9CA3AF]">
          <CatalogIcon catalogueId={entry.id} fallback={Icon} className="h-6 w-6" />
          <span className="text-[clamp(11px,0.86vw,14px)] font-medium text-black-logo">
            {entry.name}
          </span>
        </span>
        <PriceAndInfo price={entry.price} note={entry.priceNote} />
      </button>
    </div>
  );
}

// ── Face Match photo-source gating ─────────────────────────────────────────────
// Face Match compares the applicant's selfie against a REFERENCE photo captured by
// an earlier step. Only some APIs return that photo:
//   • Aadhaar → DigiLocker (with / without PIN) or Secure QR   (NOT OCR)
//   • ID Verification → Driving Licence                        (NOT Voter ID)
// So Face Match may be added only once one of these APIs is ENABLED on a step in
// the workflow. Face Match is NOT greyed out — the editor shows a toast on click
// telling the admin to add the required step first.
export const FACE_MATCH_PHOTO_OPERATION_IDS = new Set<string>([
  'Post_Aadhaar_Digilocker_Pinless',
  'Post_Aadhaar_Digilocker_Pin',
  'Post_Aadhaar_SecureQr',
  'Post_DL_Verify',
]);

// True when some Aadhaar / ID step's PRIORITY-1 (highest-priority) enabled API is a
// photo provider. Only priority-1 counts because the session run calls it FIRST and,
// on success, never reaches the lower-priority fallbacks — so only the priority-1 API
// is guaranteed to capture the reference photo. Example: if Aadhaar OCR (no photo) is
// set to priority 1, Face Match is NOT allowed even if DigiLocker is enabled at
// priority 2. A step with no bindings yet counts as "all APIs enabled in catalogue
// (document) order", so priority 1 is that tag's first catalogue entry. Returns false
// while the catalogue is still loading (can't resolve).
export function workflowHasFaceMatchPhotoSource(
  steps: ReadonlyArray<Pick<WorkflowStepDto, 'stepType' | 'apiBindings'>>,
  catalogue: readonly ApiCatalogueEntry[] | undefined,
): boolean {
  if (!catalogue || catalogue.length === 0) {
    return false;
  }

  const operationIdByEntryId = new Map(catalogue.map((e) => [e.id, e.operationId]));

  const firstPriorityOperationId = (
    step: Pick<WorkflowStepDto, 'apiBindings'>,
    tag: string,
  ): string | undefined => {
    // Unconfigured step → the Method tab auto-populates every API in catalogue order
    // (already sorted by the document sequence), so priority 1 is the first entry.
    if (step.apiBindings.length === 0) {
      return catalogue.find((e) => e.tag === tag)?.operationId;
    }

    const firstEnabled = step.apiBindings
      .filter((b) => b.isEnabled)
      .sort((a, b) => a.priority - b.priority)[0];
    return firstEnabled ? operationIdByEntryId.get(firstEnabled.catalogueEntryId) : undefined;
  };

  return steps.some((step) => {
    const tag = STEP_TYPE_CATALOGUE_TAGS[step.stepType];
    if (tag !== 'Aadhaar Verification' && tag !== 'ID Verification') {
      return false;
    }

    const firstOp = firstPriorityOperationId(step, tag);
    return firstOp != null && FACE_MATCH_PHOTO_OPERATION_IDS.has(firstOp);
  });
}

// The message shown when a step can't be added yet. Today only Face Match has a
// dependency (a photo-source step); `photoSourceAvailable` is precomputed by the
// caller via workflowHasFaceMatchPhotoSource.
export function stepDependencyReason(
  stepType: string | undefined,
  photoSourceAvailable: boolean,
): string | null {
  if (stepType === 'face_match' && !photoSourceAvailable) {
    return 'Face Match needs a reference photo. Add an Aadhaar step with DigiLocker (with/without PIN) or Secure QR — or an ID Verification step with Driving Licence — and make sure that photo API is set as PRIORITY 1 (e.g. Aadhaar OCR as priority 1 won’t work, it returns no photo).';
  }

  return null;
}

// ── Main catalog ──────────────────────────────────────────────────────────────

type StepCatalogProps = {
  /** The workflow's steps, in order. Identity is the step id, not its type. */
  activeSteps: ActiveStep[];
  /** Id of the step whose settings are open. */
  selectedStepId: string | null;
  /** An existing step was clicked — open its settings. */
  onSelectStep: (stepId: string) => void;
  /** A picker tile was clicked — add a step of this type. */
  onAddStepType: (stepType: string) => void;
  /**
   * Takes a step back out of the workflow (the ✕ on an active step's marker).
   * Keyed by step ID, not step type: a workflow can hold several steps of one type
   * and the ✕ must remove the row it sits on, not the first of its kind.
   */
  onRemoveStep: (stepId: string) => void;
  onReorder: (orderedStepIds: string[]) => void;
  totalCost: number;
  /** Read-only (View mode): selecting an existing step to inspect it still
      works, but adding/removing/reordering steps is disabled. */
  readOnly?: boolean;
};

export function StepCatalog({
  activeSteps,
  selectedStepId,
  onSelectStep,
  onAddStepType,
  onRemoveStep,
  onReorder,
  totalCost,
  readOnly = false,
}: StepCatalogProps) {
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const filterRef = useRef<HTMLDivElement>(null);

  // Real per-API prices from the seeded catalogue (credits/call). We key by the
  // catalogue Tag a step maps to (STEP_TYPE_CATALOGUE_TAGS). A tag can hold several
  // APIs at different prices (e.g. Aadhaar: DigiLocker 7, OCR/Secure-QR 10); we take
  // the MAX so the shown cost is the worst-case for that step. Anything without a real
  // catalogue entry keeps its placeholder, so nothing breaks.
  const { data: catalogue } = useApiCatalogue();
  const priceByTag = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of catalogue ?? []) {
      // Only APIs that are actually offered count toward the shown cost — skip
      // hidden/dormant ones (e.g. OVSE, isAvailable=false) so they don't inflate
      // the max above the price of the APIs the tenant can actually use.
      if (entry.costPerCall != null && entry.isAvailable) {
        map.set(entry.tag, Math.max(map.get(entry.tag) ?? 0, entry.costPerCall));
      }
    }
    return map;
  }, [catalogue]);

  const withRealPrice = useCallback(
    (entry: CatalogEntry): CatalogEntry => {
      const tag = entry.stepType ? STEP_TYPE_CATALOGUE_TAGS[entry.stepType] : undefined;
      const real = tag ? priceByTag.get(tag) : undefined;
      return real != null ? { ...entry, price: real.toFixed(2) } : entry;
    },
    [priceByTag],
  );

  // Which catalogue tags actually exist in the DB (the APIs we provide today). The picker
  // is driven entirely by this — only real APIs show.
  const dbTags = useMemo(() => new Set((catalogue ?? []).map((e) => e.tag)), [catalogue]);

  // Tag → Console category, straight from the catalogue rows. Console owns this taxonomy
  // (api_catalogue.category mirrors catalog_categories.name), so the groups here are
  // whatever Console says they are — nothing is invented or hardcoded on this side.
  const categoryByTag = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of catalogue ?? []) {
      if (entry.category) {
        map.set(entry.tag, entry.category);
      }
    }
    return map;
  }, [catalogue]);

  const categoryOf = useCallback(
    (entry: CatalogEntry) => {
      const tag = entry.stepType ? STEP_TYPE_CATALOGUE_TAGS[entry.stepType] : undefined;
      return (tag && categoryByTag.get(tag)) || UNCATEGORISED;
    },
    [categoryByTag],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function toggleFilter(option: string) {
    setSelectedFilters((prev) =>
      prev.includes(option) ? prev.filter((f) => f !== option) : [...prev, option],
    );
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = activeSteps.map((s) => s.id);
    const oldIdx = ids.indexOf(active.id as string);
    const newIdx = ids.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(ids, oldIdx, newIdx));
  }

  // Only steps whose catalogue tag exists in the DB (the APIs we actually offer),
  // plus the handful that aren't catalogue APIs at all (see the set's comment).
  const availableEntries = STEP_CATALOG.filter((c) => {
    if (c.stepType && CATALOGUE_INDEPENDENT_STEP_TYPES.has(c.stepType)) return true;
    const tag = c.stepType ? STEP_TYPE_CATALOGUE_TAGS[c.stepType] : undefined;
    return tag != null && dbTags.has(tag);
  });

  // The categories actually present, for the Filter menu. Derived from the catalogue rather
  // than a hardcoded list, which is what let the old menu offer 18 options for 1 real
  // category — and one of those ("Face Match and Liveness") was a Console SUBcategory.
  const filterOptions = useMemo(() => {
    const names = new Set(availableEntries.map(categoryOf));
    return [...names];
  }, [availableEntries, categoryOf]);

  const term = search.trim().toLowerCase();
  const matches = (c: CatalogEntry) =>
    (!term || c.name.toLowerCase().includes(term)) &&
    (selectedFilters.length === 0 || selectedFilters.includes(categoryOf(c)));

  // Active rows follow the current step order; inactive rows are the rest. Both filtered
  // by the search term.
  //
  // One row per STEP, not per step type — a workflow can hold several Document
  // Template steps (one document each), and keying rows by type would collapse them
  // into a single row that only ever selected the first.
  const activeItems = activeSteps
    .map((step) => {
      const entry = availableEntries.find((c) => c.stepType === step.stepType);
      return entry ? { step, entry } : null;
    })
    .filter((row): row is { step: ActiveStep; entry: CatalogEntry } => row !== null)
    .filter((row) => matches(row.entry));

  // A type already in the workflow drops out of the picker, because every type is once-only —
  // EXCEPT the repeatable ones, which stay so a second (or third) can be added. Superseded types
  // never appear.
  const inactiveItems = availableEntries.filter(
    (c) =>
      matches(c) &&
      (!c.stepType ||
        (!SUPERSEDED_STEP_TYPES.has(c.stepType) &&
          (REPEATABLE_STEP_TYPES.has(c.stepType) ||
            !activeSteps.some((s) => s.stepType === c.stepType)))),
  );

  const visibleCount = activeItems.length + inactiveItems.length;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      {/* Header */}
      <div className="flex shrink-0 flex-col gap-[clamp(8px,0.8vw,10px)] border-b border-[#E5E7EB] ps-[clamp(12px,0.875vw,14px)] pb-[clamp(12px,0.875vw,14px)]">
        <h3 className="m-0 text-[clamp(14px,1.1vw,16px)] font-semibold text-black-logo">Steps</h3>

        <div className="flex items-center overflow-visible rounded-lg border border-[#E5E7EB] bg-white transition-colors focus-within:border-primary">
          <div className="relative flex flex-1 items-center">
            <Search className="pointer-events-none absolute start-3 h-3.5 w-3.5 text-[#9CA3AF]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full bg-transparent py-2 ps-9 pe-3 text-[clamp(12px,0.9vw,13px)] text-black-logo outline-none"
            />
          </div>
          <div className="h-5 w-px shrink-0 bg-[#E5E7EB]" />
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setFilterOpen((o) => !o)}
              className="flex cursor-pointer items-center gap-1 rounded-e-lg border-none bg-transparent px-3 py-2 text-[clamp(12px,0.9vw,13px)] text-gray66 outline-none transition-colors hover:bg-[#F9FAFB]"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filter{' '}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {filterOpen && (
              <div className="absolute end-0 top-[calc(100%+2px)] z-50 flex max-h-80 min-w-60 flex-col gap-1 overflow-y-auto rounded-xl border border-[#E5E7EB] bg-white p-2 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)]">
                {filterOptions.map((item) => (
                  <button
                    type="button"
                    key={item}
                    onClick={() => toggleFilter(item)}
                    className={cn(
                      'cursor-pointer rounded-lg border-none bg-transparent px-[clamp(12px,1vw,16px)] py-[clamp(6px,0.5vw,8px)] text-start text-[length:var(--text-14)] font-medium text-black-logo transition-colors hover:bg-[#F9FAFB]',
                      selectedFilters.includes(item) && 'bg-[#F4F7FB]',
                    )}
                  >
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {selectedFilters.map((filter) => (
              <button
                type="button"
                key={filter}
                onClick={() => toggleFilter(filter)}
                className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-primary bg-[#EEF2FF] px-[clamp(8px,0.6vw,12px)] py-[clamp(3px,0.25vw,4px)] text-[clamp(11px,0.8vw,12px)] font-medium text-primary"
              >
                {filter} <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Steps list — ONE flat list, in workflow order. Deliberately NOT grouped by
          category: this is the sequence being built, so a category header would break the
          numbered chain and stop a step being dragged past a boundary. The category lives
          in the Filter above instead. */}
      <div className="custom-scrollbar flex-1 overflow-y-auto py-[clamp(12px,0.875vw,14px)] pe-1.5">
        {visibleCount === 0 && (
          <p className="px-2 py-6 text-center text-[clamp(11px,0.85vw,13px)] text-gray66">
            {term || selectedFilters.length > 0 ? 'No matching APIs.' : 'No APIs available.'}
          </p>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={activeSteps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {activeItems.map(({ step, entry }, idx) => (
              <SortableActiveRow
                key={step.id}
                sortableId={step.id}
                entry={{
                  ...withRealPrice(entry),
                  ...(step.label ? { name: step.label } : {}),
                  // A step whose cost depends on its configuration overrides the
                  // catalogue price — see ActiveStep.price.
                  ...(step.price != null ? { price: step.price.toFixed(2) } : {}),
                  ...(step.priceNote ? { priceNote: step.priceNote } : {}),
                }}
                isSelected={step.id === selectedStepId}
                isLast={idx === activeItems.length - 1 && inactiveItems.length === 0}
                onSelect={() => onSelectStep(step.id)}
                onRemove={() => onRemoveStep(step.id)}
                // Counted from activeSteps, not activeItems — a search term can filter
                // the list down to one visible row while the workflow still holds
                // several, and that row must stay removable.
                canRemove={activeSteps.length > 1}
                readOnly={readOnly}
              />
            ))}
            {inactiveItems.map((entry, idx) => (
              <InactiveRow
                key={entry.id}
                entry={withRealPrice(entry)}
                isLast={idx === inactiveItems.length - 1}
                isSelected={false}
                onSelect={() => entry.stepType && onAddStepType(entry.stepType)}
                readOnly={readOnly}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Cost footer */}
      <div className="shrink-0 py-[clamp(8px,0.875vw,14px)]">
        <div className="flex items-center justify-between rounded-lg bg-[#E5F6EA] p-[clamp(8px,0.625vw,10px)] text-[length:var(--text-14)] font-normal text-black">
          Cost per completed session
          <span className="text-[length:var(--text-20)] font-bold text-[#00A92C]">
            ₹ {totalCost > 0 ? totalCost.toFixed(2) : '0.00'}
          </span>
        </div>
      </div>
    </div>
  );
}
